import {Component, Input, Output, EventEmitter, inject, DestroyRef, Inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {DialogModule} from 'primeng/dialog';
import {ButtonModule} from 'primeng/button';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import { MsfSelect } from '@components/select/select';
import {FieldExportType} from '@models/export-file-type';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FieldBaseGetQuery, FieldService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {PrimaryButton} from '@components/button/primary-button/primary-button';

@Component({
  selector: 'app-export-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, TranslateModule, MsfSelect, PrimaryButton],
  templateUrl: './export-modal.component.html',
  styleUrls: ['./export-modal.component.scss']
})
export class ExportModalComponent {
  private translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  public  isExporting = false;
  public  exportFormat!:FieldExportType;

  @Input() visible: boolean = false;
  @Input() service : any = inject(FieldService);
  @Input() exportMethod = 'cropMonitoringFieldExportFieldsPost';
  @Input() exportCsvMethod = 'cropMonitoringFieldExportFieldsPost';
  @Input() filtersParams: Object[] = [];

  @Input() exportFormatOptions =
    [
    {
      label: this.translate.instant('Shapefile'),
      value: FieldExportType.ShapeFile
    },
    {label: 'KML', value: FieldExportType.KML},
    {label: 'GeoJSON', value: FieldExportType.GeoJson},
    {label: 'Csv', value: FieldExportType.Csv},
  ];

  @Output() visibleChange = new EventEmitter<boolean>();



  hide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  startExport(): void {
    const format =  this.exportFormat;
    this.isExporting = true;
    const filters = this.filtersParams;

    const exportCall$ = format === FieldExportType.Csv
      ? this.exportAsCSV(filters)
      : this.exportByFormat(filters, format);

    exportCall$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response:any) => {
          this.handleExportResponse(response, format);
          this.isExporting = false;
        },
        error: (err:any) => {
          console.error('Export failed:', err);
          this.isExporting = false;
        }
      });
  }

  private exportAsCSV(filters: any) {
    const params: FieldBaseGetQuery = {
      filters: filters || []
    };

    return this.service[this.exportCsvMethod](
      params,
      'body',
      false,
      { httpHeaderAccept: 'application/octet-stream' }
    );
  }

  private exportByFormat(filters: any, format: FieldExportType ) {
    const params = this.buildExportParams(filters, format);

    return this.service[this.exportMethod](
      params as any,
      'body',
      false,
      { httpHeaderAccept: 'application/octet-stream' }
    );
  }

  private buildExportParams(filters: Object[], format: FieldExportType): unknown {
    return {
      fieldsQuery: { filters },
      fieldExportType: format
    };
  }

  private handleExportResponse(response: unknown, format: FieldExportType): void {
    if (response instanceof Blob) {
      this.downloadFile(response, format);
      return;
    }

    const asAny = response as { body?: Blob };
    if (asAny?.body instanceof Blob) {
      this.downloadFile(asAny.body, format);
    }
  }

  private downloadFile(blob: Blob, format: FieldExportType): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fields-export${this.getExtensionForFormat(format)}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }

  private getExtensionForFormat(format: FieldExportType): string {
    const extensions: Record<FieldExportType, string> = {
      [FieldExportType.GeoJson]: '.zip',
      [FieldExportType.KML]: '.zip',
      [FieldExportType.Csv]: '.csv',
      [FieldExportType.ShapeFile]: '.zip'
    };
    return extensions[format] || '';
  }


}
